import React, { useEffect, useState } from 'react';

import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

export type BreakpointMatch = boolean | undefined;

export default function useMatchMedia(query: string): BreakpointMatch {
  // Initialize state with undefined so server and client renders match
  const [isMatch, setIsMatch] = useState<BreakpointMatch>(undefined);

  useEffect(() => {
    // we know window exists because it's inside useEffect(), which only runs on the client
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    const mediaQueryList = window.matchMedia(query);
    if (mediaQueryList.media === 'not all') {
      captureError(
        new Error(
          `The media query "${query}" is invalid, and cannot be parsed. Did you forget to add 'px'?`
        )
      );
    }

    // Handler to update match state
    const handleChange = (e): void => {
      setIsMatch(e.matches);
    };

    // Add event listener
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
    } else {
      mediaQueryList.addListener(handleChange);
    }

    // set state right away with initial match
    setIsMatch(mediaQueryList.matches);

    // Remove event listener on cleanup
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleChange);
      } else {
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, [query]);

  return isMatch;
}

interface WithMatchesMediaProps {
  matchesMediaQuery?: BreakpointMatch;
}

// component wrapper for use with Class components
export const withMatchMedia = (query: string) => {
  return <P extends WithMatchesMediaProps = WithMatchesMediaProps>(
    Component: React.ComponentType<P>
  ): React.FC<Omit<React.ComponentProps<typeof Component>, 'matchesMediaQuery'>> => {
    function WrappedComponent(props) {
      const matches = useMatchMedia(query);
      return <Component {...props} matchesMediaQuery={matches} />;
    }

    WrappedComponent.displayName = `MatchMedia(${
      Component.displayName || Component.name || 'Component'
    })`;

    return WrappedComponent;
  };
};
